<script setup lang="ts">
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/20/solid'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const emit = defineEmits(['dismiss'])
const { t } = useI18n()
type TwAlertPositions = 'right' | 'left'
interface TwAlertPropsInterface {
  headline: string
  items?: string[]
  type?: string
  sticky?: boolean
  disableDismiss?: boolean
  position?: TwAlertPositions
}
const props = withDefaults(defineProps<TwAlertPropsInterface>(), {
  disableDismiss: false,
  headline: 'SOMETHING_WENT_WRONG',
  type: 'error',
  sticky: true,
  position: 'right',
  items() {
    return []
  },
})
function dismiss() {
  emit('dismiss')
}
const displayHeadline = computed(() => {
  if (props.headline && props.headline.length) {
    if (props.headline.toUpperCase() === props.headline) {
      return t(props.headline)
    } else {
      return props.headline
    }
  } else {
    return null
  }
})
const displayItems = computed(() => {
  const ret = [] as string[]
  if (props.items && props.items.length) {
    props.items.forEach((item) => {
      const displayItem = item && item.length
        ? item.toUpperCase() === item
          ? t(item)
          : item
        : null
      if (displayItem) {
        ret.push(displayItem)
      }
    })
  }
  return ret
})
</script>

<template>
  <div
    :class="{
      'bg-red-50 dark:bg-red-900': type === 'error',
      'bg-green-50 dark:bg-green-900': type === 'success',
      'bg-yellow-50 dark:bg-yellow-900': type === 'warning',
      'bg-blue-50 dark:bg-blue-900': type === 'info',
      'z-50 sm:bottom-[100px] fixed sm:right-20 right-0 bottom-0': sticky,
      'sm:right-20': position === 'right',
      'sm:left-20': position === 'left',
    }"
    class="rounded-md p-4 w-full max-w-screen-sm shadow-sm"
  >
    <div class="flex">
      <div class="flex-shrink-0">
        <CheckCircleIcon
          v-if="type === 'success'"
          class="h-5 w-5 text-green-400"
          aria-hidden="true"
        />
        <ExclamationTriangleIcon
          v-else-if="type === 'warning' || type === 'error'"
          class="h-5 w-5"
          :class="{ 'text-yellow-400': type === 'warning', 'text-red-500': type === 'error' }"
          aria-hidden="true"
        />
        <InformationCircleIcon
          v-else
          class="h-5 w-5 text-blue-400"
          aria-hidden="true"
        />
      </div>
      <div class="ml-3">
        <h3
          v-if="displayHeadline"
          :class="{
            'text-red-800 dark:text-red-50': type === 'error',
            'text-green-800 dark:text-green-50': type === 'success',
            'text-yellow-800 dark:text-yellow-50': type === 'warning',
            'text-blue-800 dark:text-blue-50': type === 'info',
          }"
          class="text-sm font-medium"
        >
          {{ displayHeadline }}
        </h3>
        <div
          v-if="displayItems.length"
          :class="{
            'text-red-700 dark:text-red-100': type === 'error',
            'text-green-700 dark:text-green-100': type === 'success',
            'text-yellow-700 dark:text-yellow-100': type === 'warning',
            'text-blue-700 dark:text-blue-100': type === 'info',
          }"
          class="mt-2 text-sm"
        >
          <ul
            role="list"
            class="list-disc space-y-1 pl-5"
          >
            <li
              v-for="(item, key) in displayItems"
              :key="key"
            >
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
      <div
        v-if="!disableDismiss"
        class="ml-auto pl-3"
      >
        <div class="-mx-1.5 -my-1.5">
          <button
            type="button"
            class="inline-flex rounded-md p-1.5 text-slate-700 dark:text-white"
            @click="dismiss()"
          >
            <span class="sr-only">{{ t('DISMISS_ALERT') }}</span>
            <XMarkIcon
              class="h-5 w-5"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
